// Locations screen — list of mining locations with zone, level, AP count,
// priority badge. Mirrors src/screens/locations/LocationListScreen.tsx
// summary pattern (Total / Active APs / Personnel Detected).

import React, {useMemo, useState} from 'react'
import clsx from 'clsx'
import {useNavigate} from 'react-router-dom'
import {Pencil, Plus} from 'lucide-react'
import {SearchBar} from '../components/common/SearchBar'
import {SummaryCard} from '../components/common/SummaryCard'
import {useSupabaseSubscription} from '../app/useSupabaseSubscription'
import type {APDetail, LocationDetail} from './location/types'

interface LocationRow extends LocationDetail {}

interface APRow extends APDetail {}

const PRIORITY_BADGE: Record<NonNullable<LocationRow['priority']>, string> = {
  CRITICAL: 'bg-badgeRedBg    text-statusRed',
  HIGH:     'bg-badgeOrangeBg text-statusOrange',
  MEDIUM:   'bg-badgeYellowBg text-statusYellow',
}

export const LocationsScreen: React.FC = () => {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')

  const {rows: locations, loading: lLoading, error: lError} =
    useSupabaseSubscription<LocationRow>({
      table: 'locations',
      columns:
        'id, name, zone, level, type, description, priority, material, heading_status, ' +
        'next_blast_date, tonnes_remaining, x, y, z',
      orderBy: ['name', true],
    })

  const {rows: aps, loading: aLoading, error: aError} =
    useSupabaseSubscription<APRow>({
      table: 'access_points',
      columns:
        'id, location_id, name, mac_address, ssid, ip_address, is_active, status, ' +
        'x, y, z, level, radius_m',
    })

  // Personnel-present count is derived from the most recent location_event
  // per personnel — if last event was entry_in for this location, they're
  // present. For V1 we just count entry_in events as a proxy (cheap).
  interface LocationEvent {
    id: string
    location_id: string
    personnel_id: string
    event_type: 'entry_in' | 'entry_out'
    timestamp: string
  }
  const {rows: locEvents} = useSupabaseSubscription<LocationEvent>({
    table: 'location_events',
    columns: 'id, location_id, personnel_id, event_type, timestamp',
    scopedToMine: false,
    events: ['INSERT', 'DELETE'],
  })

  const personnelPresentByLocation = useMemo(() => {
    // For each (personnel_id, location_id) keep the latest event.
    const latest = new Map<string, LocationEvent>()
    for (const e of locEvents) {
      const key = `${e.personnel_id}::${e.location_id}`
      const prev = latest.get(key)
      if (!prev || Date.parse(e.timestamp) > Date.parse(prev.timestamp)) {
        latest.set(key, e)
      }
    }
    const counts = new Map<string, number>()
    for (const e of latest.values()) {
      if (e.event_type !== 'entry_in') continue
      counts.set(e.location_id, (counts.get(e.location_id) ?? 0) + 1)
    }
    return counts
  }, [locEvents])

  const apsByLocation = useMemo(() => {
    const map = new Map<string, APRow[]>()
    for (const a of aps) {
      if (!a.location_id) continue
      const arr = map.get(a.location_id) ?? []
      arr.push(a)
      map.set(a.location_id, arr)
    }
    return map
  }, [aps])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return locations
    return locations.filter(l =>
      `${l.name} ${l.zone ?? ''} ${l.material ?? ''}`.toLowerCase().includes(q),
    )
  }, [locations, search])

  const summary = useMemo(() => {
    const totalLocations = locations.length
    let activeAPs = 0
    for (const a of aps) if (a.is_active) activeAPs++
    // Aggregate the per-location entry counts already computed above so
    // the "Personnel Detected" card matches the tablet's summary value.
    let personnelDetected = 0
    for (const n of personnelPresentByLocation.values()) personnelDetected += n
    return {totalLocations, activeAPs, personnelDetected}
  }, [locations, aps, personnelPresentByLocation])

  if (lLoading || aLoading) {
    return <div className="flex h-full items-center justify-center text-textMuted">Loading locations…</div>
  }
  if (lError || aError) {
    return (
      <div className="flex h-full items-center justify-center p-xxxl">
        <div className="rounded-lg border border-statusRed bg-surfaceCard p-xxl">
          <h3 className="mb-md text-lg font-semibold text-statusRed">Fetch failed</h3>
          <pre className="whitespace-pre-wrap font-mono text-sm text-textPrimary">{lError || aError}</pre>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-background">
      {/* Controls — searchRow inside surface card + Add Location button. */}
      <div className="px-xl py-md">
        <div className="flex items-center gap-lg">
          <div className="flex flex-1 items-center gap-lg rounded-lg bg-surface p-sm">
            <div className="flex-1">
              <SearchBar value={search} onChange={setSearch} placeholder="Search.." />
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate('/locations/add')}
            className="flex items-center gap-sm rounded-md bg-primary px-xl py-md text-md font-bold text-black hover:bg-primaryLight"
          >
            <Plus size={16} color="black" strokeWidth={3} />
            Add Location
          </button>
        </div>
      </div>

      {/* Summary — px-xl gap-md mb-lg with colored left accents. */}
      <div className="mb-lg grid grid-cols-3 gap-md px-xl">
        <SummaryCard
          label="Total Locations"
          value={summary.totalLocations}
          accentColor="var(--color-primary)"
        />
        <SummaryCard
          label="Active APs"
          value={summary.activeAPs}
          valueClass="text-statusGreen"
          accentColor="var(--color-statusGreen)"
        />
        <SummaryCard
          label="Personnel Detected"
          value={summary.personnelDetected}
          accentColor="var(--color-statusBlue)"
        />
      </div>

      {/* Table — adds Personnel column matching mobile LocationListScreen:
          name 2fr / zone 0.5 / priority 0.8 / ap 0.4 / material 0.6 / heading 0.6 / personnel 0.6 / action 60px.
          Zone / APs / Material / Heading are rendered in bold per UI feedback. */}
      <div className="mx-xl mb-xl flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-surfaceBorder bg-surfaceCard">
        <div className="grid shrink-0 grid-cols-[2fr_0.5fr_0.8fr_0.4fr_0.6fr_0.6fr_0.6fr_60px] gap-md items-center border-b border-divider bg-surface px-lg py-md text-xs font-semibold uppercase text-textMuted">
          <div>Name</div>
          <div>Zone</div>
          <div>Priority</div>
          <div className="pr-[62px] text-right">APs</div>
          <div>Material</div>
          <div>Heading</div>
          <div className="pr-[62px] text-right">Personnel</div>
          <div className="text-center">Actions</div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {filtered.map(l => {
            const apCount = (apsByLocation.get(l.id) ?? []).length
            const personnelCount = personnelPresentByLocation.get(l.id) ?? 0
            return (
              <button
                key={l.id}
                type="button"
                onClick={() => navigate(`/locations/${l.id}`)}
                className={clsx(
                  'grid w-full grid-cols-[2fr_0.5fr_0.8fr_0.4fr_0.6fr_0.6fr_0.6fr_60px] gap-md items-center px-lg py-md text-left',
                  'border-b border-divider hover:bg-surfaceLight/40 focus:bg-surfaceLight/60 focus:outline-none',
                )}
              >
                <div className="truncate text-md font-semibold text-textPrimary">{l.name}</div>
                <div className="text-md font-semibold text-textPrimary">{l.zone ?? '—'}</div>
                <div>
                  {l.priority ? (
                    <span
                      className={clsx(
                        'rounded-sm px-sm py-xs text-xs font-semibold uppercase',
                        PRIORITY_BADGE[l.priority],
                      )}
                    >
                      {l.priority}
                    </span>
                  ) : (
                    <span className="text-sm text-textMuted">—</span>
                  )}
                </div>
                <div className="pr-[62px] text-right text-md font-semibold text-textPrimary tabular-nums">
                  {apCount}
                </div>
                <div className="text-md font-semibold text-textPrimary">{l.material ?? '—'}</div>
                <div className="text-md font-semibold text-textPrimary">{l.heading_status ?? '—'}</div>
                <div className="pr-[62px] text-right text-md font-bold text-textPrimary tabular-nums">{personnelCount}</div>
                <div className="flex justify-center">
                  <span
                    role="button"
                    tabIndex={-1}
                    onClick={ev => {
                      ev.stopPropagation()
                      navigate(`/locations/${l.id}/edit`)
                    }}
                    className="flex h-9 w-9 items-center justify-center rounded-md border border-surfaceBorder text-textPrimary hover:border-primary hover:text-primary"
                    aria-label={`Edit ${l.name}`}
                  >
                    <Pencil size={14} />
                  </span>
                </div>
              </button>
            )
          })}
          {filtered.length === 0 && (
            <div className="px-lg py-xxxl text-center text-textMuted">
              No locations match "{search}".
            </div>
          )}
        </div>
      </div>
      <div className="h-md" />
    </div>
  )
}
